import React, { useState, useRef } from 'react';
import { Microphone, Stop, SpinnerGap } from '@phosphor-icons/react';

export default function VoiceMicButton({ api, onResponse }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      mediaRecorder.current.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        await processAudio(blob);
      };

      mediaRecorder.current.start();
      setRecording(true);
      setResult(null);
    } catch (err) {
      console.error('Microphone access denied:', err);
      setResult({ error: 'Microphone access denied. Please allow microphone permissions.' });
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current?.state === 'recording') {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  };

  const processAudio = async (blob) => {
    setProcessing(true);
    try {
      // Step 1: Transcribe
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      const transcribeRes = await api.post('/voice-cmd/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const transcript = transcribeRes.data.text;

      // Step 2: Process command
      const processRes = await api.post('/voice-cmd/process', { text: transcript });
      const response = { transcript, ...processRes.data };
      setResult(response);

      // Speak response using TTS
      if (response.response && 'speechSynthesis' in window) {
        const utter = new SpeechSynthesisUtterance(response.response);
        utter.lang = 'en-IN';
        utter.rate = 0.95;
        window.speechSynthesis.speak(utter);
      }

      if (onResponse) onResponse(response);
    } catch (err) {
      console.error('Voice processing error:', err);
      setResult({ error: err.response?.data?.detail || 'Voice processing failed' });
    }
    setProcessing(false);
  };

  return (
    <div data-testid="voice-mic-widget" className="relative">
      {/* Mic Button */}
      <button
        onClick={recording ? stopRecording : startRecording}
        disabled={processing}
        data-testid="mic-button"
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
          recording
            ? 'bg-red-500 animate-pulse shadow-red-500/40'
            : processing
              ? 'bg-neutral-700 cursor-wait'
              : 'bg-gradient-to-br from-[#FF6B35] to-[#E85D2C] hover:shadow-[#FF6B35]/40 hover:scale-105'
        }`}
      >
        {processing ? (
          <SpinnerGap size={24} className="text-white animate-spin" />
        ) : recording ? (
          <Stop size={24} weight="fill" className="text-white" />
        ) : (
          <Microphone size={24} weight="fill" className="text-white" />
        )}
      </button>

      {/* Recording indicator */}
      {recording && (
        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-ping" />
      )}

      {/* Result popup */}
      {result && !recording && !processing && (
        <div className="absolute bottom-full mb-3 right-0 w-80 glass-card p-4 animate-fade-up z-50" data-testid="voice-result">
          {result.error ? (
            <p className="text-sm text-red-400">{result.error}</p>
          ) : (
            <>
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">You said</p>
                <p className="text-sm text-white font-medium">{result.transcript}</p>
              </div>
              <div className="border-t border-white/5 pt-2">
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">HomeCommand</p>
                <p className="text-sm text-neutral-300">{result.response}</p>
              </div>
              {result.action && result.action !== 'none' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] font-semibold">{result.action.replace('_', ' ')}</span>
                </div>
              )}
            </>
          )}
          <button onClick={() => setResult(null)} className="absolute top-2 right-2 text-neutral-500 hover:text-white text-xs">dismiss</button>
        </div>
      )}
    </div>
  );
}
